"use client";

import { setClientToken } from "@onsand/api";
import { auth } from "@onsand/auth";
import { signInWithEmailAndPassword } from "firebase/auth";
import { CircleAlert, Eye, EyeOff, Loader2, Lock, Mail } from "lucide-react";
import { useTranslations } from "next-intl";
import { type FormEvent, useState } from "react";
import {
	AUTH_FIELD as FIELD,
	AUTH_FIELD_ICON as FIELD_ICON,
	AUTH_LABEL as LABEL,
} from "@/components/auth/field-styles";
import { SocialButtons } from "@/components/auth/social-buttons";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import { Link, useRouter } from "@/i18n/navigation";
import type { SocialErrorKey } from "@/lib/camp/auth/auth-errors";
import { cn } from "@/lib/utils";
import { ForgotPassword } from "./forgot-password";
import { type SignInErrorKey, signInErrorKey } from "./sign-in-error";

export function LoginForm({ className }: { className?: string }) {
	const router = useRouter();
	const t = useTranslations("login");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState<SignInErrorKey | SocialErrorKey | null>(null);
	const [loading, setLoading] = useState(false);
	const [resetting, setResetting] = useState(false);

	async function handleSubmit(e: FormEvent) {
		e.preventDefault();
		setLoading(true);
		setError(null);

		try {
			const credential = await signInWithEmailAndPassword(auth, email, password);
			setClientToken(await credential.user.getIdToken());
			router.push("/");
		} catch (err) {
			setError(signInErrorKey(err));
			// Only a failure frees the button: on success the page is already on its way out.
			setLoading(false);
		}
	}

	return (
		<section
			aria-labelledby="login-title"
			className={cn("rounded-2xl bg-card p-6 shadow-xs/5 sm:p-8", className)}
		>
			{resetting ? (
				<ForgotPassword initialEmail={email} onBack={() => setResetting(false)} />
			) : (
				<div className="w-full">
					<h1
						className="text-balance font-heading font-semibold text-3xl leading-tight tracking-tight"
						id="login-title"
					>
						{t("title")}
					</h1>
					<p className="mt-2 text-base text-muted-foreground">{t("signInDescription")}</p>

					<div className="mt-8">
						<SocialButtons onError={setError} onSignedIn={() => router.push("/")} />
					</div>
					<div className="my-6 flex items-center gap-3 text-muted-foreground text-sm">
						<span className="h-px flex-1 bg-border" />
						{t("orWithEmail")}
						<span className="h-px flex-1 bg-border" />
					</div>

					<form className="grid gap-5" onSubmit={handleSubmit}>
						<div className="grid gap-2">
							<Label className={LABEL} htmlFor="email">
								{t("email")}
							</Label>
							<InputGroup className={FIELD}>
								<InputGroupAddon>
									<Mail aria-hidden="true" className={FIELD_ICON} />
								</InputGroupAddon>
								<InputGroupInput
									autoCapitalize="none"
									autoComplete="username"
									id="email"
									inputMode="email"
									name="email"
									onChange={(e) => setEmail(e.target.value)}
									placeholder={t("emailPlaceholder")}
									required
									spellCheck={false}
									type="email"
									value={email}
								/>
							</InputGroup>
						</div>

						<div className="grid gap-2">
							<Label className={LABEL} htmlFor="password">
								{t("password")}
							</Label>
							<InputGroup className={FIELD}>
								<InputGroupAddon>
									<Lock aria-hidden="true" className={FIELD_ICON} />
								</InputGroupAddon>
								<InputGroupInput
									autoComplete="current-password"
									id="password"
									name="password"
									onChange={(e) => setPassword(e.target.value)}
									required
									type={showPassword ? "text" : "password"}
									value={password}
								/>
								<InputGroupAddon align="inline-end">
									<Button
										aria-label={showPassword ? t("hidePassword") : t("showPassword")}
										className="size-11 text-muted-foreground hover:text-foreground sm:size-11"
										onClick={() => setShowPassword((visible) => !visible)}
										size="icon"
										variant="ghost"
									>
										{showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
									</Button>
								</InputGroupAddon>
							</InputGroup>
						</div>

						<Button
							className="-mt-2 h-11 justify-self-start px-0 text-muted-foreground hover:bg-transparent hover:text-foreground sm:h-9"
							onClick={() => setResetting(true)}
							type="button"
							variant="ghost"
						>
							{t("forgotPassword")}
						</Button>

						{error && (
							<p
								className="flex items-start gap-2 text-pretty text-destructive-foreground text-sm"
								role="alert"
							>
								<CircleAlert aria-hidden="true" className="mt-0.5 size-4 shrink-0" />
								{t(error)}
							</p>
						)}

						<Button
							className="mt-1 h-12 w-full rounded-full font-semibold text-base sm:h-12 sm:text-base"
							disabled={loading}
							type="submit"
						>
							{loading ? (
								<>
									<Loader2 aria-hidden="true" className="size-5 animate-spin" />
									{t("signingIn")}
								</>
							) : (
								t("signIn")
							)}
						</Button>
					</form>

					<p className="mt-6 text-center text-muted-foreground text-sm">
						{t("noAccountYet")}{" "}
						<Link
							className="inline-flex min-h-11 items-center font-medium text-foreground underline underline-offset-4 hover:text-primary sm:min-h-8"
							href="/signup"
						>
							{t("signUpLink")}
						</Link>
					</p>
				</div>
			)}
		</section>
	);
}
